<template>
    <nav
        v-if="sectionVisibility[SectionsVisibilityType.BREADCRUMBS]"
        class="breadcrumb-wrapper-content"
        :aria-label="$t('generic.breadcrumb.aria.label')"
        data-testid="profile-breadcrumb-wrapper"
    >
        <ol v-if="breadcrumb" class="breadcrumb-wrapper-content-breadcrumb">
            <li class="breadcrumb-wrapper-content-breadcrumb__item">
                <a :href="$withLocalePrefix('/')">{{ $t('navigation.breadcrumb.home') }}</a>
            </li>
            <li
                v-if="hasFamily"
                class="breadcrumb-wrapper-content-breadcrumb__item m-link m-link_clear"
                :class="{'no-cursor': !familyHref}"
                data-testid="profile-breadcrumb-family"
            >
                <a v-if="familyHref" :href="familyHref" class="m-link m-link_clear">
                    {{ familyLabel }}
                </a>
                <span v-else>{{ familyLabel }}</span>
            </li>
            <li
                v-if="hasCategory"
                class="breadcrumb-wrapper-content-breadcrumb__item"
                :class="{'no-cursor': !categoryHref}"
                data-testid="profile-breadcrumb-category"
            >
                <a v-if="categoryHref" :href="categoryHref">
                    {{ categoryLabel }}
                </a>
                <span v-else>{{ categoryLabel }}</span>
            </li>
        </ol>
    </nav>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useLocalePrefix} from '#imports';
    import {useProfileStore} from '~/store/profileStore';
    import {useSectionsVisibilityStore} from '~/store/sectionsVisibility';
    import {getCategorySeoUrl, getFamilySeoUrl} from '~/types/domain/Breadcrumb';
    import {SectionsVisibilityType} from '~/types/domain/SectionsVisibility';

    const profileStore = useProfileStore();
    const {breadcrumb} = storeToRefs(profileStore);
    const {withLocalePrefix} = useLocalePrefix();

    const {sectionVisibility} = storeToRefs(useSectionsVisibilityStore());

    const hasFamily = computed<boolean>(() => breadcrumb.value!.levels.family !== undefined);
    const hasCategory = computed<boolean>(() => breadcrumb.value!.levels.category !== undefined);
    const familyHref = computed(() => {
        const seoUrl = getFamilySeoUrl(breadcrumb.value!);
        return seoUrl ? withLocalePrefix(seoUrl) : undefined;
    });
    const categoryHref = computed(() => {
        const seoUrl = getCategorySeoUrl(breadcrumb.value!);
        return seoUrl ? withLocalePrefix(seoUrl) : undefined;
    });
    const familyLabel = computed<string | undefined>(() => breadcrumb.value!.levels.family?.label);
    const categoryLabel = computed<string | undefined>(() => breadcrumb.value!.levels.category?.label);
</script>
<style lang="scss" scoped>
    .breadcrumb-wrapper-content {
        display: flex;
        height: 100%;
        max-width: 100%;

        &-breadcrumb {
            display: inline-block;
            padding: 0;
            list-style-type: none;
            margin: auto 0;
            margin-bottom: var(--joy-core-spacing-6);

            &__item {
                color: var(--joy-color-neutral-40);
                font-size: var(--joy-font-size-primary-300);
                vertical-align: middle;
                position: relative;
                display: inline-block;
                padding-right: 20px;
                height: 100%;

                &:last-child a {
                    color: var(--joy-color-neutral-60);
                }

                &:not(:last-child)::before {
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    right: 5px;
                    content: '>';
                    font-size: var(--joy-font-size-primary-300);
                }

                a {
                    color: var(--joy-color-neutral-40);
                    text-decoration: none;

                    &:hover {
                        text-decoration: underline;
                    }
                }
            }
        }
    }

    .no-cursor {
        cursor: default;
    }
</style>
